/** Same 6-month window: page vs typical SERP date must be within this gap to count as "similar". */
export const FRESHNESS_SIX_MONTHS_MS = 183 * 24 * 60 * 60 * 1000;
export const FRESHNESS_TWO_YEARS_MS = 730 * 24 * 60 * 60 * 1000;

export type FreshnessSerpRelation = 'similar' | 'newer' | 'older' | 'unknown';

export interface FreshnessSerpCompareResult {
  relation: FreshnessSerpRelation;
  serpTypicalDate: Date | null;
  serpTypicalDateLabel: string | null;
  verdict: string | null;
}

export function medianPublishedDate(sortedDates: Date[]): Date | null {
  if (!sortedDates.length) return null;
  const mid = Math.floor(sortedDates.length / 2);
  if (sortedDates.length % 2 === 1) return sortedDates[mid]!;
  return new Date((sortedDates[mid - 1]!.getTime() + sortedDates[mid]!.getTime()) / 2);
}

export function formatFreshnessDateLabel(d: Date): string {
  try {
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

/** Compare crawl last-modified vs typical top-SERP publication date (median of dated winners). */
export function comparePageFreshnessToSerp(
  pageDate: Date | null,
  serpDates: Date[],
  _nowMs = Date.now()
): FreshnessSerpCompareResult {
  const sorted = [...serpDates].sort((a, b) => a.getTime() - b.getTime());
  const median =
    sorted.length >= 2
      ? medianPublishedDate(sorted)
      : sorted.length === 1
        ? sorted[0]!
        : null;
  const serpTypicalDateLabel = median ? formatFreshnessDateLabel(median) : null;

  if (!pageDate && !median) {
    return { relation: 'unknown', serpTypicalDate: null, serpTypicalDateLabel: null, verdict: null };
  }
  if (!pageDate && median) {
    return {
      relation: 'unknown',
      serpTypicalDate: median,
      serpTypicalDateLabel,
      verdict: 'Compare your last content update when you have a crawl date.'
    };
  }
  if (pageDate && !median) {
    return {
      relation: 'unknown',
      serpTypicalDate: null,
      serpTypicalDateLabel: null,
      verdict: 'Top SERP rows lack enough publication dates to compare freshness.'
    };
  }

  // Compare calendar gap between your last update and typical SERP date (not “both old from today”).
  const pageToSerpGapMs = median!.getTime() - pageDate!.getTime();

  if (pageToSerpGapMs >= FRESHNESS_SIX_MONTHS_MS) {
    return {
      relation: 'older',
      serpTypicalDate: median,
      serpTypicalDateLabel,
      verdict: 'Your page looks older than typical top results.'
    };
  }
  if (pageToSerpGapMs <= -FRESHNESS_SIX_MONTHS_MS) {
    return {
      relation: 'newer',
      serpTypicalDate: median,
      serpTypicalDateLabel,
      verdict: 'Your page looks newer than typical top results.'
    };
  }
  return {
    relation: 'similar',
    serpTypicalDate: median,
    serpTypicalDateLabel,
    verdict: 'Your page and typical top results are in a similar freshness band.'
  };
}

export interface SerpPublishedDateRow {
  position: number;
  publishedAt?: string | null;
}

export function serpWinnerPublishedDatesFromResults(
  results: SerpPublishedDateRow[] | null | undefined
): Date[] {
  const dates: Date[] = [];
  for (const row of results || []) {
    if (row.position < 1 || row.position > 7) continue;
    if (!row.publishedAt) continue;
    try {
      const d = new Date(row.publishedAt);
      if (!Number.isNaN(d.getTime())) dates.push(d);
    } catch {
      /* skip invalid */
    }
  }
  return dates;
}

export function parsePageContentLastModifiedDate(raw: unknown): Date | null {
  if (!raw) return null;
  const d = raw instanceof Date ? raw : new Date(String(raw));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function pageContentLastModifiedLabel(pageDate: Date | null, rawFallback?: unknown): string {
  if (!pageDate) {
    if (rawFallback != null && rawFallback !== '') return String(rawFallback);
    return 'No last-update date on crawl';
  }
  return formatFreshnessDateLabel(pageDate);
}

export function freshnessDiagnosisLabel(
  pageDate: Date | null,
  serpDates: Date[],
  nowMs = Date.now()
): string {
  if (!pageDate) return 'No date';
  const ageMs = nowMs - pageDate.getTime();
  if (ageMs >= FRESHNESS_TWO_YEARS_MS) return 'Stale';
  const compare = comparePageFreshnessToSerp(pageDate, serpDates, nowMs);
  if (compare.relation === 'older') return 'Behind SERP';
  if (compare.relation === 'newer') return 'Ahead of SERP';
  if (ageMs >= FRESHNESS_SIX_MONTHS_MS) return 'Aging';
  return 'Recent';
}
